import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    BooleanField,
    EditButton,
    ShowButton,
    Filter,
    SearchInput,
    ReferenceField,
    useRecordContext,
} from 'react-admin';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    ReferenceInput,
} from 'react-admin';
import {
    Show,
    SimpleShowLayout,
} from 'react-admin';
import QRCodeDisplay from '../components/QRCodeDisplay';
import { getApiUrl } from '../utils/apiUrl';

const HomeFilter = () => (
    <Filter>
        <SearchInput source="address" alwaysOn />
        <SearchInput source="city" />
        <SearchInput source="state" />
        <SearchInput source="zip_code" />
    </Filter>
);

const QRCodeButton = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    // Construct the correct URL for the main app
    const getAppUrl = () => {
        const origin = window.location.origin;
        if (origin.includes('localhost')) {
            // Development: use localhost:3000
            return 'http://localhost:3000';
        } else if (origin.includes('admin.neverknock.com')) {
            // Production: admin.neverknock.com -> app.neverknock.com
            return 'https://app.neverknock.com';
        } else {
            // Fallback: remove /admin and use same domain
            return origin.replace('/admin', '');
        }
    };
    
    const qrCodeUrl = `${getAppUrl()}/offer/${record.uuid}`;
    
    return (
        <button
            onClick={async () => {
                try {
                    // Import qrcode library for server-side generation
                    const QRCode = require('qrcode');
                    
                    // Generate QR code as data URL
                    const dataUrl = await QRCode.toDataURL(qrCodeUrl, { 
                        width: 200, 
                        margin: 2,
                        color: {
                            dark: '#000000',
                            light: '#FFFFFF'
                        }
                    });
                    
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                        printWindow.document.write(`
                            <html>
                                <head>
                                    <title>QR Code - ${record.address || 'Home'}</title>
                                    <style>
                                        body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
                                        .qr-container { margin: 20px 0; }
                                        .qr-code { display: inline-block; padding: 20px; border: 1px solid #ccc; }
                                        .address { font-size: 14px; color: #666; margin: 10px 0; }
                                        .url { font-size: 12px; color: #999; word-break: break-all; }
                                    </style>
                                </head>
                                <body>
                                    <h2>Door QR Code</h2>
                                    <p class="address">${record.address || ''}, ${record.city || ''}, ${record.state || ''} ${record.zip_code || ''}</p>
                                    <div class="qr-container">
                                        <div class="qr-code">
                                            <img src="${dataUrl}" alt="QR Code" style="width: 200px; height: 200px;" />
                                        </div>
                                    </div>
                                    <p class="url">${qrCodeUrl}</p>
                                </body>
                            </html>
                        `);
                        printWindow.document.close();
                        
                        // Wait a moment for the image to load before printing
                        setTimeout(() => {
                            printWindow.print();
                        }, 100);
                    }
                } catch (error) {
                    console.error('Error generating QR code:', error);
                }
            }}
            style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                marginRight: '8px',
                display: 'inline-block'
            }}
        >
            QR Code
        </button>
    );
};

interface HomeServiceItem {
    uuid: string;
    home_uuid: string;
    service_uuid: string;
    relationship_type: string;
    service?: {
        uuid: string;
        name: string;
        category: string;
        description: string;
        is_active: boolean;
    };
}

const ViewServicesButton = () => {
    const record = useRecordContext();
    const [isOpen, setIsOpen] = useState(false);
    const [services, setServices] = useState<HomeServiceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!record) return null;

    const apiUrl = getApiUrl();

    const fetchServices = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${apiUrl}/api/v1/admin/homes/${record.uuid}/services`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch services');
            }

            const data = await response.json();
            setServices(data.items || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load services');
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        fetchServices();
    };

    const handleClose = () => {
        setIsOpen(false);
        setServices([]);
        setError(null);
    };

    const formatRelationshipType = (type: string) => {
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    handleOpen();
                }}
                style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginRight: '8px',
                    display: 'inline-block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                }}
            >
                View Services
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    onClick={handleClose}
                >
                    <div
                        style={{
                            backgroundColor: '#1e1e1e',
                            borderRadius: '8px',
                            padding: '24px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                            border: '1px solid #333',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 500, color: '#ffffff' }}>
                                Home Services
                            </h2>
                            <button
                                onClick={handleClose}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#9e9e9e',
                                    padding: '4px',
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <p style={{ color: '#9e9e9e', fontSize: '14px', marginBottom: '20px' }}>
                            {record.address}, {record.city}, {record.state} {record.zip_code}
                        </p>

                        {loading && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#9e9e9e' }}>
                                Loading services...
                            </div>
                        )}

                        {error && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#f44336' }}>
                                {error}
                            </div>
                        )}

                        {!loading && !error && services.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#9e9e9e' }}>
                                No services associated with this home.
                            </div>
                        )}

                        {!loading && !error && services.length > 0 && (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#2d2d2d' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #424242', color: '#90caf9', fontWeight: 500 }}>Service</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #424242', color: '#90caf9', fontWeight: 500 }}>Category</th>
                                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #424242', color: '#90caf9', fontWeight: 500 }}>Relationship</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {services.map((hs) => (
                                        <tr key={hs.uuid} style={{ borderBottom: '1px solid #333' }}>
                                            <td style={{ padding: '12px', color: '#ffffff' }}>{hs.service?.name || 'Unknown'}</td>
                                            <td style={{ padding: '12px', color: '#bdbdbd' }}>{hs.service?.category || '-'}</td>
                                            <td style={{ padding: '12px' }}>
                                                <span
                                                    style={{
                                                        backgroundColor:
                                                            hs.relationship_type === 'installed' ? '#1b5e20' :
                                                            hs.relationship_type === 'outsourced' ? '#e65100' :
                                                            '#1565c0',
                                                        color: '#ffffff',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {formatRelationshipType(hs.relationship_type)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button
                                onClick={handleClose}
                                style={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '8px 20px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const HomeList = () => (
    <List filters={<HomeFilter />}>
        <Datagrid>
            <TextField source="id" />
            <ReferenceField source="user_uuid" reference="users">
                <TextField source="email" />
            </ReferenceField>
            <TextField source="address" />
            <TextField source="city" />
            <TextField source="state" />
            <TextField source="zip_code" />
            <TextField source="property_type" />
            <TextField source="year_built" />
            <TextField source="square_footage" />
            <TextField source="bedrooms" />
            <TextField source="bathrooms" />
            <BooleanField source="has_basement" />
            <BooleanField source="has_garage" />
            <BooleanField source="has_pool" />
            <BooleanField source="has_solar" />
            <DateField source="created_at" />
            <ViewServicesButton />
            <QRCodeButton />
            <EditButton />
            <ShowButton />
        </Datagrid>
    </List>
);

export const HomeEdit = () => (
    <Edit>
        <SimpleForm>
            <ReferenceInput source="user_uuid" reference="users" />
            <TextInput source="address" />
            <TextInput source="city" />
            <TextInput source="state" />
            <TextInput source="zip_code" />
            <TextInput source="property_type" />
            <TextInput source="year_built" />
            <TextInput source="square_footage" />
            <TextInput source="bedrooms" />
            <TextInput source="bathrooms" />
            <BooleanInput source="has_basement" />
            <BooleanInput source="has_garage" />
            <BooleanInput source="has_pool" />
            <BooleanInput source="has_solar" />
            <TextInput source="preferred_contact_method" />
            <TextInput source="preferred_contact_time" />
            <TextInput source="notes" multiline rows={3} />
        </SimpleForm>
    </Edit>
);

const QRCodeSection = () => {
    const record = useRecordContext();
    
    if (!record) {
        return <div style={{ padding: '20px', border: '2px solid red', margin: '20px' }}>No record available</div>;
    }
    
    const fullAddress = `${record.address || ''}, ${record.city || ''}, ${record.state || ''} ${record.zip_code || ''}`.trim();
    
    return (
        <QRCodeDisplay 
            homeUuid={record.id || record.uuid || ''} 
            address={fullAddress}
        />
    );
};

export const HomeShow = () => (
    <Show>
        <SimpleShowLayout>
            <TextField source="id" />
            <ReferenceField source="user_uuid" reference="users">
                <TextField source="email" />
            </ReferenceField>
            <TextField source="address" />
            <TextField source="city" />
            <TextField source="state" />
            <TextField source="zip_code" />
            <TextField source="property_type" />
            <TextField source="year_built" />
            <TextField source="square_footage" />
            <TextField source="bedrooms" />
            <TextField source="bathrooms" />
            <BooleanField source="has_basement" />
            <BooleanField source="has_garage" />
            <BooleanField source="has_pool" />
            <BooleanField source="has_solar" />
            <TextField source="preferred_contact_method" />
            <TextField source="preferred_contact_time" />
            <TextField source="notes" />
            <DateField source="created_at" />
            <DateField source="updated_at" />
            
            {/* QR Code Section */}
            <QRCodeSection />
        </SimpleShowLayout>
    </Show>
); 
